/**
 * URL query param asosidagi super app hostlar uchun factory.
 *
 * Zoomrad, va kelajakdagi shunga o'xshash hostlar `?code=...` shaklida
 * callback URL bilan ochiladi. Bu factory shu pattern ni yagonalashtiradi.
 *
 * Yangi vendor qo'shish — bu fayl ga teginmasdan, faqat `defineQueryParamHost({...})`
 * ni chaqirib `src/hosts/index.ts` ga ro'yxatga olish kifoya.
 */

import { apiPost, apiPostBeacon } from "@/api/client";
import { setToken, clearToken, getToken, isTokenValid } from "@/lib/auth";
import type { BrandId } from "@/config/uiTokens";
import type { HostAdapter, HostId, LoginResponse, VendorLoginResponse } from "./types";
import { mapVendorResponse } from "./types";

export interface QueryParamHostConfig {
  id: HostId;
  brandId: BrandId;
  /** Super app ning domeni (mas. "zoomrad.insonuchun.uz") */
  hostnames: readonly string[];
  /** Backenddagi login endpoint (mas. "/MiniApp/ZoomradLogin") */
  loginEndpoint: string;
  /** URL query param nomi — shu parametr qiymat olinadi (mas. "code") */
  queryParam: string;
  /** Backendga jo'natiladigan JSON payload kaliti (mas. "code") */
  payloadKey: string;
  /**
   * Ixtiyoriy logout endpoint.
   * Ko'rsatilsa, `pagehide` eventi da fire-and-forget POST jo'natiladi.
   */
  logoutEndpoint?: string;
}

export function defineQueryParamHost(config: QueryParamHostConfig): HostAdapter {
  const hostnameSet = new Set(config.hostnames);

  return {
    id: config.id,
    brandId: config.brandId,

    match() {
      if (typeof window === "undefined") return false;
      return hostnameSet.has(window.location.hostname);
    },

    async login(): Promise<LoginResponse> {
      const params = new URLSearchParams(window.location.search);
      const code = params.get(config.queryParam);

      // ?code yo'q = reload, mavjud token sessionStorage da — skip
      if (!code) {
        const existing = getToken();
        if (isTokenValid(existing)) {
          return { token: existing!, phoneNumber: "", fullName: "" };
        }
        // Token yo'q yoki eskirgan — code ham yo'q, login imkonsiz
        throw new Error("Zoomrad: session expired. Please reopen the app.");
      }

      // ?code bor = yangi sessiya: eski tokenni tozalab yangi olamiz
      clearToken();

      const raw = await apiPost<VendorLoginResponse>(config.loginEndpoint, {
        [config.payloadKey]: code,
      });
      setToken(raw.token);

      // Muvaffaqiyatli login — ?code ni URL dan olib tashlaymiz (reload yo'q)
      params.delete(config.queryParam);
      const newSearch = params.toString();
      const cleanUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
      window.history.replaceState(null, "", cleanUrl);

      return mapVendorResponse(raw);
    },

    logout() {
      // App yopilganda sessionStorage ni tozalaymiz — keyingi kirishda
      // yangi ?code bilan qayta login bo'lishi uchun
      clearToken();
      if (config.logoutEndpoint) {
        apiPostBeacon(config.logoutEndpoint);
      }
    },
  };
}
